import React, { useEffect, useState } from "react";
import { collection, onSnapshot, query, orderBy, limit, documentId } from "firebase/firestore";
import { db } from "../firebase";
import Navbar from "../component/Navbar";
import { ensureMetricsToggleListener, fetchDailyReadsEnabledOnce, setDailyReadsEnabled } from "../config/metricsToggle";
import { Switch } from "@mui/material";

export default function DailyReads() {
  const [rows, setRows] = useState([]);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    ensureMetricsToggleListener();
    fetchDailyReadsEnabledOnce().then(setEnabled).catch(() => setEnabled(true));
    const q = query(
      collection(db, "dailyReads"),
      orderBy(documentId(), "desc"),
      limit(14)
    );
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRows(data);
    });
    return () => unsub();
  }, []);

  const renderRow = (row) => {
    const entries = Object.entries(row).filter(([k]) => k !== "id");
    const groups = {
      clients: [],
      notes: [],
      profile: [],
      roles: [],
      other: [],
    };
    for (const [k, v] of entries) {
      if (k.startsWith("clients_")) groups.clients.push([k, v]);
      else if (k.startsWith("notes_")) groups.notes.push([k, v]);
      else if (k.startsWith("profile_")) groups.profile.push([k, v]);
      else if (k === "roles") groups.roles.push([k, v]);
      else if (k === "total") { /* handled below */ }
      else groups.other.push([k, v]);
    }
    const chip = (k, v) => (
      <div key={k} style={{
        background: "#f5f5f7",
        padding: "6px 10px",
        borderRadius: 4,
        fontSize: 13
      }}>
        {k}: {String(v)}
      </div>
    );
    const section = (title, arr) => (
      arr.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {arr.map(([k, v]) => chip(k, v))}
          </div>
        </div>
      )
    );
    return (
      <div key={row.id} style={{
        border: "1px solid #ddd",
        borderRadius: 6,
        padding: 12,
        marginBottom: 10,
        background: "white"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 600 }}>{row.id}</div>
          {typeof row.total === "number" && (
            <div style={{ color: "#333" }}>total: {row.total}</div>
          )}
        </div>
        {section("Clients", groups.clients)}
        {section("Notes", groups.notes)}
        {section("Client Profile", groups.profile)}
        {section("Roles", groups.roles)}
        {section("Other", groups.other)}
      </div>
    );
  };

  const keyColors = [
    "#1976d2", "#2e7d32", "#d32f2f", "#f9a825", "#6a1b9a",
    "#00897b", "#5d4037", "#0288d1", "#7b1fa2", "#c2185b",
  ];
  const colorForKey = (k) => {
    let h = 0;
    for (let i = 0; i < k.length; i++) h = (h * 31 + k.charCodeAt(i)) >>> 0;
    return keyColors[h % keyColors.length];
  };

  const renderPieFromEntries = (title, entries) => {
    if (!entries || entries.length === 0) return null;
    const sorted = [...entries].sort((a, b) => b[1] - a[1]);
    const total = sorted.reduce((a, [, v]) => a + v, 0) || 1;
    let angle = 0;
    const radius = 90;
    const cx = 120, cy = 120;
    const toXY = (ang) => [cx + radius * Math.cos(ang), cy + radius * Math.sin(ang)];
    const slices = sorted.map(([k, v]) => {
      const sliceAngle = (v / total) * Math.PI * 2;
      const start = angle;
      const end = angle + sliceAngle;
      const [x1, y1] = toXY(start);
      const [x2, y2] = toXY(end);
      const large = sliceAngle > Math.PI ? 1 : 0;
      angle = end;
      const d = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} Z`;
      const color = colorForKey(k);
      const pct = Math.round((v / total) * 100);
      return { d, color, label: k, value: v, pct };
    });
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
        <svg width={260} height={260} viewBox={`0 0 ${cx*2} ${cy*2}`} aria-label={title}>
          {slices.map((s, i) => (
            <path key={i} d={s.d} fill={s.color} stroke="#fff" strokeWidth={1} />
          ))}
        </svg>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div>
          {slices.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <span style={{ width: 12, height: 12, background: s.color, borderRadius: 2 }} />
              <span>{s.label}: {s.value} ({s.pct}%)</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="main-container">
      <Navbar />
      <div className="right-container" style={{ padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h2 style={{ marginTop: 0, marginBottom: 0 }}>Daily Firestore Reads</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Switch
              checked={enabled}
              onChange={async (e) => {
                const v = e.target.checked;
                setEnabled(v);
                try { await setDailyReadsEnabled(v); } catch {}
              }}
              size="small"
            />
            <span
              title={`Counters are ${enabled ? "enabled" : "disabled"}`}
              style={{
                padding: "4px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                color: enabled ? "#0b5" : "#b00",
                background: enabled ? "#e8f8ef" : "#fdeeee",
                border: `1px solid ${enabled ? "#9ed7b5" : "#f1b3b3"}`,
              }}
            >
              {enabled ? "Counters Enabled" : "Counters Disabled"}
            </span>
          </div>
        </div>
        <div style={{ color: "#666", marginBottom: 12 }}>
          Live counters by day and entity (metrics DB)
        </div>
        {rows[0] && (() => {
          const row = rows[0];
          const entries = Object.entries(row).filter(([k, v]) => typeof v === "number" && k !== "total");
          const clients = entries.filter(([k]) => k.startsWith("clients_"));
          const notes = entries.filter(([k]) => k.startsWith("notes_"));
          const profile = entries.filter(([k]) => k.startsWith("profile_"));
          return (
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {renderPieFromEntries("Clients", clients)}
              {renderPieFromEntries("Notes", notes)}
              {renderPieFromEntries("Client Profile", profile)}
            </div>
          );
        })()}
        {rows.map(renderRow)}
        {rows.length === 0 && <div>No data yet.</div>}
      </div>
    </div>
  );
}
